import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Palette, Globe, Video, Share2, Camera, Car, PartyPopper, Package, Monitor, PenTool, BarChart3, Users, Newspaper } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ScrollReveal from "@/components/ScrollReveal";
import SEO from "@/components/SEO";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";

const sections = [
  {
    id: "medienproduktion",
    icon: Video,
    title: "Medienproduktion",
    description: "Film, Foto & alles dazwischen. Wir bringen deine Geschichte in Bild & Ton, ob in Bochum, NRW oder deutschlandweit.",
    items: [
       { icon: Video, title: "Imagefilm & Videoproduktion", text: "Wir fangen den Kern Ihrer Marke mit unserer Videoproduktion ein in Kinoqualität." },
      { icon: Camera, title: "Fotoshootings & Produktfotografie", text: "Professionelle Foto- und Produktfotografie für Print, Web und Social Media. Jedes Bild erzählt eine Geschichte." },
       { icon: PartyPopper, title: "Eventbegleitung", text: "Von der Firmenfeier bis zum Großevent wir begleiten euch und liefern emotionale Aftermovies, die in Erinnerung bleiben." },
    ],
    packages: [
      { name: "Imagefilm & Videoproduktion", description: "Wir erzählen die Geschichte Ihrer Marke visuell hochwertig, authentisch und auf den Punkt.", includes: ["Konzept & Storytelling", "Hochwertige Videoproduktion", "Lizenzfreie Musik", "Bereitstellung in Full HD"] },
      { name: "Fotoshootings & Produktfotografie", description: "Starke Bilder schaffen Vertrauen und verkaufen. Wir setzen Ihre Produkte, Ihr Team oder Ihre Marke perfekt in Szene modern, hochwertig und zielgerichtet.", includes: ["Hochwertige Produktfotografie", "Content für Web, Print & Social Media", "Produkt- & Detailaufnahmen", "Moodboard & Bildsprache-Entwicklung", "Kreativkonzept & visuelle Markenstrategie"] },
      { name: "Eventbegleitung", description: "Wir halten die besonderen Momente Ihres Events fest emotional, dynamisch und authentisch. So bleibt Ihr Event nicht nur in Erinnerung, sondern wird erlebbar.", includes: ["Highlight-Video / Aftermovie", "Flexible Einsatzdauer", "Schnelle Bereitstellung der Inhalte", "Drohnenaufnahmen", "Konzeptentwicklung & Unterstützung bei der Planung"] },
    ],
  },
  {
    id: "corporate-identity",
    icon: Palette,
    title: "Corporate Identity",
    description: "Dein Markenauftritt – durchdacht, konsistent und unverwechselbar.",
    items: [
      { icon: PenTool, title: "Logo & Branding", text: "Wir entwickeln Logos und visuelle Identitäten, die deine Marke einzigartig machen und in Erinnerung bleiben." },
      { icon: Newspaper, title: "Geschäftsausstattung", text: "Visitenkarten, Briefpapier, Verpackungsdesign alles aus einem Guss für einen professionellen Auftritt." },
    ],
    packages: [
      { name: "Branding Basic", description: "Das starke Fundament für deinen Markenauftritt. \nFür einen klaren, professionellen Start mit allem, was du wirklich brauchst.", includes: ["Logo-Design (2 Entwürfe)", "Farbpalette & Typografie", "Logo-Dateien in allen Formaten"] },
      { name: "Business", description: "Strategisches Wachstum für Marken, die mehr wollen.\nFür einen durchdachten, konsistenten Auftritt mit wieder erkennungswert.", includes: ["Alles aus Branding Basic", "Gestaltung & Druckabwicklung von Druckprodukten", "Brand Strategie", "Brand Guidelines"] },
      { name: "Enterprise", description: "Der ganzheitliche Markenauftritt mit klarer Vision. Für Marken mit Anspruch, die Design als strategisches Werkzeug nutzen.", includes: ["Alles aus Business", "Komplette Geschäftsausstattung", "Verpackungsdesign", "Social-Media-Templates", "Brand Book"] },
    ],
  },
  {
    id: "webseiten",
    icon: Globe,
    title: "Webseiten-Erstellung",
    description: "Moderne, performante Webseiten aus Bochum, die überzeugen & konvertieren. Für Unternehmen in ganz Deutschland.",
    items: [
       { icon: Monitor, title: "Responsive Design", text: "Jede Webseite funktioniert auf jedem Gerät vom Smartphone bis zum Desktop. Kein Nachgedanke, sondern Grundlage von Anfang an." },
      { icon: Globe, title: "CMS & E-Commerce", text: "Inhalte selbst pflegen, Produkte verwalten, Bestellungen abwickeln. Wir bauen Systeme die ihr ohne Entwickler bedienen könnt." },
    ],
    packages: [
      { name: "ONEPAGER", description: "Alles auf einer Seite kompakt, übersichtlich und auf den Punkt.", includes: ["Einzelseite mit durchdachter Seitenstruktur", "Responsive Design für alle Endgeräte", "SEO-Grundoptimierung für lokale Sichtbarkeit", "Kontaktmöglichkeit und rechtssichere Pflichtseiten"] },
      { name: "Business Web", description: "Für Unternehmen die mehr zeigen wollen als eine Seite hergibt.", includes: ["Bis zu 7 Unterseiten (erweiterbar) - Content-Management-System zum selbst pflegen", "SEO-Optimierung für organische Reichweite", "Performance-Optimierung für schnelle Ladezeiten"] },
      { name: "E-Commerce", description: "Ein Online-Shop der verkauft nicht nur Produkte auflistet.", includes: ["Individuelles Shop-Design mit Fokus auf Nutzerführung", "Produktverwaltung und Zahlungsabwicklung", "Mobile Optimierung mit Conversion-Fokus", "SEO & GEO Performance", "3 Monate technischer Support nach Launch"] },
    ],
  },
  {
    id: "social-media",
    icon: Share2,
    title: "Social Media Marketing",
    description: "Strategie, Content & Community-Management für echte Reichweite. Social Media Agentur aus Bochum, bundesweit aktiv.",
    items: [
      { icon: BarChart3, title: "Strategie & Analyse", text: "Datengetriebene Social-Media-Strategien, die messbare Ergebnisse liefern und deine Zielgruppe erreichen." },
       { icon: Users, title: "Content & Betreuung", text: "Kreativer Content, Community & Account-Management und regelmäßige Reportings alles aus einer Hand." },
       { icon: Video, title: "Content Produktion", text: "Sie wollen Ihren Social Media Auftritt selbst steuern, aber nicht auf professionellen Content verzichten? Wir liefern Ihnen maßgeschneiderte Foto- und Videoinhalte bereit zur direkten Verwendung auf Ihren Kanälen." },
    ],
      packages: [
        { name: "Social Starter", description: "Ideal für den Einstieg in Social Media mit regelmäßigem Content.", includes: ["\nContent-Plan & Strategie", "Account- & Community-Management", "Regelmäßige Postings (Basis-Frequenz)", "Community & Account-Management", "Monatliches Reporting & Analyse"] },
        { name: "Social Growth", description: "Mehr Reichweite und Engagement durch strategisches Community & Account Management.", includes: ["Content-Plan & Strategie", "Account- & Community-Management", "Erhöhte Posting-Frequenz", "Community & Account-Management", "Monatliches Reporting & Analyse"] },
        { name: "Social Premium", description: "Skalierbares Social Media Management für maximale Reichweite, Wachstum und starke Markenpräsenz.", includes: ["Content-Strategie & Planung", "\n\nAccount- & Community-Management", "Erhöhte Posting-Frequenz", "Alle Plattformen", "Monatliches Reporting & Analyse", "Tägliches Monitoring"] },
      ],
  },
];

const Leistungen = () => {
  const { hash } = useLocation();

  useEffect(() => {
    if (hash) {
      setTimeout(() => {
        const el = document.querySelector(hash);
        el?.scrollIntoView({ behavior: "smooth" });
      }, 300);
    }
  }, [hash]);

  return (
    <div className="min-h-screen bg-background">
      <SEO title="Leistungen – Kreativagentur Bochum | Videoproduktion, Webdesign & Branding deutschlandweit" description="Unsere 360° Leistungen: Videoproduktion, Webdesign, Corporate Identity & Social Media Marketing. Kreativagentur aus Bochum, deutschlandweit im Einsatz." path="/leistungen" />
      <Header />

      {/* Hero */}
      <section className="pt-32 pb-20 bg-gradient-hero">
        <div className="container mx-auto px-4">
          <ScrollReveal>
            <span className="text-primary text-sm font-medium tracking-wider uppercase">Was wir machen</span>
            <h1 className="text-5xl md:text-7xl font-bold text-foreground mt-4 mb-6">
              Unsere <span className="text-gradient">Leistungen</span>
            </h1>
              <p className="text-xl text-foreground/85 max-w-2xl">
                Das volle 360°-Programm: von der Idee bis zur Umsetzung, alles unter einem Dach. Aus Bochum, für Kunden in ganz Deutschland.
              </p>
          </ScrollReveal>
        </div>
      </section>

      {/* 360° Banner */}
      <section className="py-16 border-y border-border">
        <div className="container mx-auto px-4 text-center">
          <ScrollReveal>
            <div className="p-10 rounded-3xl bg-gradient-card border border-primary/20">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                Das <span className="text-gradient">360° Komplettpaket</span>
              </h2>
              <p className="text-foreground/85 max-w-2xl mx-auto">
                Warum vier Agenturen beauftragen, wenn eine alles kann? Unser Rundum-Paket vereint CI, Webdesign, Videoproduktion & Social Media Marketing. Perfekt abgestimmt, aus einer Hand und deutschlandweit verfügbar.
              </p>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Service Sections */}
      {sections.map((section, sIdx) => (
        <section key={section.id} id={section.id} className={`py-28 ${sIdx % 2 === 1 ? "bg-card/30" : ""}`}>
          <div className="container mx-auto px-4">
            <ScrollReveal>
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                  <section.icon size={24} className="text-primary" />
                </div>
                <span className="text-primary text-sm font-medium tracking-wider uppercase">{section.title}</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-4">{section.title}</h2>
                <p className="text-lg text-foreground/85 max-w-2xl mb-16">{section.description}</p>
            </ScrollReveal>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {section.items.map((item, i) => (
                <ScrollReveal key={item.title} delay={i * 100}>
                  <div className="p-8 rounded-2xl bg-gradient-card border border-border/50 hover:border-primary/20 transition-all duration-300 h-full">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-5">
                      <item.icon size={22} className="text-primary" />
                    </div>
                    <h3 className="text-xl font-semibold text-foreground mb-3">{item.title}</h3>
                    <p className="text-foreground/85 text-base leading-relaxed">{item.text}</p>
                  </div>
                </ScrollReveal>
              ))}
            </div>

            {/* Leistungspakete */}
            <ScrollReveal delay={200}>
              <div className="mt-16">
                <h3 className="text-2xl font-bold text-foreground mb-6">Unser Service</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {section.packages.map((pkg, pIdx) => (
                    <div key={pkg.name} className="p-6 rounded-2xl border border-border/50 bg-gradient-card hover:border-primary/20 transition-all duration-300 flex flex-col">
                      <div className="flex flex-col items-start gap-1 mb-3">
                       <span className="text-primary font-bold text-base">{String(pIdx + 1).padStart(2, "0")}</span>
                         <span className="font-semibold text-xl text-foreground" style={{ fontFamily: '"agency-fb", sans-serif', fontWeight: 900, fontStyle: 'italic', textTransform: 'uppercase' }}>{pkg.name}</span>
                       </div>
                       <p className="text-foreground/85 text-base mb-5 whitespace-pre-line">{pkg.description}</p>
                      <ul className="space-y-2 mt-auto">
                        {pkg.includes.map((inc) => (
                          <li key={inc} className="flex items-center gap-2 text-base text-foreground/85">
                            <span className="w-1.5 h-1.5 rounded-full bg-primary shrink-0" />
                            {inc}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            </ScrollReveal>
          </div>
        </section>
      ))}

      <Footer />
    </div>
  );
};

export default Leistungen;